import {AfterViewInit, Directive, ElementRef, NgZone, OnDestroy} from '@angular/core';

@Directive({
  selector: '[emojiCategoryNavigation]',
  standalone: true
})
export class EmojiCategoryNavigationDirective implements AfterViewInit, OnDestroy {
  private observer: MutationObserver | null = null;
  private categoryAnchors: Element[] = [];
  private scrollArea: Element | null = null;

  constructor(private el: ElementRef, private ngZone: NgZone) {}

  ngAfterViewInit(): void {
    // Setup mutation observer to detect when emoji categories are loaded
    this.ngZone.runOutsideAngular(() => {
      this.observer = new MutationObserver(mutations => {
        // Look for the anchors (category icons) and the scroll area
        const anchors = this.el.nativeElement.querySelectorAll('.emoji-mart-anchor');
        const scrollArea = this.el.nativeElement.querySelector('.emoji-mart-scroll');

        if (anchors.length > 0 && scrollArea) {
          this.categoryAnchors = Array.from(anchors);
          this.scrollArea = scrollArea;
          this.setupCategoryNavigation();

          // Don't disconnect the observer to handle dynamic updates
          // Instead, just keep track of elements we've already processed
        }
      });

      // Start observing the emoji-mart component for changes
      this.observer.observe(this.el.nativeElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'name']
      });
    });
  }

  private setupCategoryNavigation(): void {
    // Remove existing handlers first to prevent duplicates
    this.categoryAnchors.forEach(anchor => {
      anchor.removeEventListener('click', this.handleCategoryClick);
      anchor.removeEventListener('touchend', this.handleCategoryClick); // Changed to use same handler

      // Add both click and touch handlers for better device compatibility
      anchor.addEventListener('click', this.handleCategoryClick);
      anchor.addEventListener('touchend', this.handleCategoryClick); // Changed to use same handler
    });
  }

  private handleCategoryClick = (event: Event): void => {
    event.preventDefault();
    this.navigateToCategory(event.currentTarget as HTMLElement);
  }

  private navigateToCategory(anchorElement: HTMLElement): void {
    const categoryName = anchorElement.getAttribute('data-name');
    if (!categoryName || !this.scrollArea) return;

    // Find the corresponding category section - try multiple selectors for robustness
    const selectors = [
      `.emoji-mart-category[name="${categoryName}"]`,
      `.emoji-mart-category[data-name="${categoryName}"]`,
      `.emoji-mart-category-label[data-name="${categoryName}"]`
    ];

    let categorySection: Element | null = null;

    for (const selector of selectors) {
      categorySection = this.el.nativeElement.querySelector(selector);
      if (categorySection) break;
    }

    // If we still can't find the category, search by its containing section
    if (!categorySection) {
      const sections = this.el.nativeElement.querySelectorAll('.emoji-mart-category');
      for (const section of sections) {
        const label = section.querySelector('.emoji-mart-category-label');
        if (label && label.getAttribute('data-name') === categoryName) {
          categorySection = section;
          break;
        }
      }
    }

    if (categorySection) {
      this.ngZone.run(() => {
        // Calculate the scroll position including any offsets
        const scrollTop = categorySection.getBoundingClientRect().top -
                         this.scrollArea!.getBoundingClientRect().top +
                         this.scrollArea!.scrollTop;

        // Use scrollTo for smoother behavior with offset adjustment
        this.scrollArea!.scrollTo({
          top: scrollTop - 5, // Small offset to improve visibility
          behavior: 'smooth'
        });
      });
    }
  }

  ngOnDestroy(): void {
    // Clean up all event listeners
    if (this.categoryAnchors.length) {
      this.categoryAnchors.forEach(anchor => {
        anchor.removeEventListener('click', this.handleCategoryClick);
        anchor.removeEventListener('touchend', this.handleCategoryClick);
      });
    }

    // Clean up the observer when directive is destroyed
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
}
